/**
 * qnnp-finance 통합 API 클라이언트
 *
 * 설계 원칙 (기존 qnnp-react-ts의 교훈 반영):
 * 1. 단일 클라이언트 — stockApiClient/callAuthApi 이중 구조로 인한 `.data` 혼동 버그 원천 제거
 * 2. 언랩 1회 — axios response.data 까지만 언랩하고, {code, message, data} 래핑 여부는
 *    `unwrapData()` 헬퍼로 명시적으로만 처리. 각 도메인 API 함수가 최종 타입만 반환
 * 3. 401 → refresh → 재시도 — BaseApiClient 싱글턴 락 패턴 승계 (동시 refresh 방지)
 * 4. iOS ITP 대응 — localStorage에 refreshToken 백업은 하지 않고 쿠키 우선.
 *    (stock-api의 쿠키 도메인 정책 유지)
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import config from "src/config";

// ── 유틸 ──

export const paramsSerializer = (params: Record<string, any>): string =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => {
      let value = v;
      if (Array.isArray(value)) value = value.join(",");
      else if (typeof value === "object") value = JSON.stringify(value);
      return `${encodeURIComponent(k)}=${encodeURIComponent(String(value))}`;
    })
    .join("&");

export class ApiError extends Error {
  status: number;
  response: any;
  constructor(message: string, status: number, response?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.response = response;
  }
}

// ── 응답 래핑 타입 ──

export interface ResponseVO<T = {}> {
  code: number;
  message: string;
  data: T;
}

/**
 * stock-api 응답 정규화:
 * - {code, message, data} 래핑이면 data 반환
 * - 이미 언랩된 페이로드면 그대로 반환
 * 두 포맷이 섞여 있는 기존 백엔드(RawApiResponse vs ResponseMiddleware)를 모두 지원.
 */
export function unwrapData<T>(res: any): T {
  if (
    res &&
    typeof res === "object" &&
    "data" in res &&
    "code" in res &&
    (typeof res.code === "number" || res.code === "SUCCESS")
  ) {
    return res.data as T;
  }
  return res as T;
}

// ── 클라이언트 ──

const DEFAULT_TIMEOUT = 30000;

class ApiClient {
  private axiosInstance: AxiosInstance;
  private static isRefreshing = false;
  private static refreshSubscribers: ((success: boolean) => void)[] = [];
  private static _onSessionExpired: (() => void) | null = null;

  constructor(axiosConfig: AxiosRequestConfig = {}) {
    this.axiosInstance = axios.create({
      baseURL: config.serverUrl,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
      timeout: DEFAULT_TIMEOUT,
      paramsSerializer: (params) => paramsSerializer(params),
      ...axiosConfig,
    });
    this.setupInterceptors();
  }

  static setOnSessionExpired(cb: () => void) {
    ApiClient._onSessionExpired = cb;
  }

  private static notifySubscribers(success: boolean) {
    ApiClient.refreshSubscribers.forEach((cb) => cb(success));
    ApiClient.refreshSubscribers = [];
    ApiClient.isRefreshing = false;
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.response.use(
      (response) => response.data,
      async (error: AxiosError) => {
        const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        if (original?.url?.includes("/login") || original?.url?.includes("/refresh")) {
          return Promise.reject(this.toApiError(error));
        }
        if (error.response?.status === 401 && original && !original._retry) {
          if (ApiClient.isRefreshing) {
            return new Promise((resolve, reject) => {
              ApiClient.refreshSubscribers.push((success) => {
                if (success) resolve(this.axiosInstance(original));
                else reject(error);
              });
            });
          }
          original._retry = true;
          ApiClient.isRefreshing = true;
          try {
            await this.axiosInstance.post("/v1/auth/refresh");
            ApiClient.notifySubscribers(true);
            return this.axiosInstance(original);
          } catch (refreshErr) {
            ApiClient.notifySubscribers(false);
            ApiClient._onSessionExpired?.();
            return Promise.reject(this.toApiError(refreshErr as AxiosError));
          } finally {
            ApiClient.isRefreshing = false;
          }
        }
        return Promise.reject(this.toApiError(error));
      },
    );
  }

  private toApiError(err: any): ApiError {
    if (err instanceof ApiError) return err;
    if (err?.response) {
      const message = err.response?.data?.message || err.response?.statusText || err.message;
      return new ApiError(message, err.response.status, err.response);
    }
    return new ApiError(err?.message || "Network error", 0, null);
  }

  /** 래핑 여부와 무관하게 최종 페이로드 반환 (unwrap + 에러 정규화) */
  async request<T>(method: "get" | "post" | "put" | "patch" | "delete", url: string, opts?: {
    params?: Record<string, any>;
    body?: any;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const res = await this.axiosInstance.request<any, any>({
        method,
        url,
        params: opts?.params,
        data: opts?.body,
        ...(opts?.config || {}),
      });
      // interceptor가 response.data를 반환하므로 res는 이미 body
      return unwrapData<T>(res);
    } catch (err: any) {
      throw this.toApiError(err);
    }
  }

  get = <T>(url: string, params?: Record<string, any>, axiosConfig?: AxiosRequestConfig) =>
    this.request<T>("get", url, { params, config: axiosConfig });

  post = <T>(url: string, body?: any, axiosConfig?: AxiosRequestConfig) =>
    this.request<T>("post", url, { body, config: axiosConfig });

  put = <T>(url: string, body?: any, axiosConfig?: AxiosRequestConfig) =>
    this.request<T>("put", url, { body, config: axiosConfig });

  patch = <T>(url: string, params?: Record<string, any>, body?: any, axiosConfig?: AxiosRequestConfig) =>
    this.request<T>("patch", url, { params, body, config: axiosConfig });

  delete = <T>(url: string, params?: Record<string, any>, axiosConfig?: AxiosRequestConfig) =>
    this.request<T>("delete", url, { params, config: axiosConfig });
}

export { ApiClient };
export const apiClient = new ApiClient();
